// Lead-gen wizard — anonymous analytics tracking.
//
// Buffers events in memory and flushes them to /api/public/wizard-analytics
// in batches on step advance and before unload. Analytics never block the
// wizard: every send is fire-and-forget via `sendBeacon` (or fetch keepalive
// as a fallback).
//
// Session identity is a random UUID minted once per browser tab and held in
// sessionStorage so the same session can be joined across step advances.
// No PII is captured here — see the matching backend module
// kitchenos-api/modules/wizard_analytics_ingest.js for the privacy
// invariants the endpoint enforces.

export type WizardEventType =
  | "range_viewed"
  | "range_image_viewed"
  | "colour_selected"
  | "range_selected"
  | "budget_selected"
  | "timeline_selected"
  | "worktop_selected"
  | "step_completed"
  | "wizard_submitted";

export interface WizardEvent {
  eventType: WizardEventType;
  rangeId?: string;
  rangeName?: string;
  colourName?: string;
  /** Budget band / timeline label / worktop label / step number (string). */
  value?: string;
  supplierId?: string;
}

const ENDPOINT = "/api/public/wizard-analytics";
const SESSION_KEY = "tkos.wizard.sessionId";
const MAX_BATCH = 50;

interface QueuedEvent extends WizardEvent {
  retailerId: string;
  sessionId: string;
}

let retailerId: string | null = null;
let queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let unloadWired = false;

function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  // Fallback for older browsers — non-RFC-compliant but unique enough.
  return (
    "s-" +
    Math.random().toString(36).slice(2, 10) +
    "-" +
    Date.now().toString(36)
  );
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = uuid();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage unavailable (privacy mode etc.) — mint per-pageload.
    return uuid();
  }
}

function wireUnloadFlush(): void {
  if (unloadWired) return;
  unloadWired = true;
  // `pagehide` is the modern unload event — fires on tab close + iOS bfcache.
  window.addEventListener("pagehide", () => { flush(); }, { capture: true });
  // `visibilitychange` catches the user backgrounding the tab without closing.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

/** Initialise the tracker with the retailer slug the wizard is mounted for.
 *  Safe to call multiple times — only the first call latches the retailer. */
export function initAnalytics(retailerSlug: string): void {
  if (!retailerSlug) return;
  if (!retailerId) retailerId = retailerSlug;
  wireUnloadFlush();
}

export function track(event: WizardEvent): void {
  if (!retailerId) return; // not yet initialised — silently drop
  const sessionId = getSessionId();
  queue.push({
    ...event,
    retailerId,
    sessionId,
  });
  if (queue.length >= MAX_BATCH) {
    flush();
  } else {
    // Coalesce: flush after a short idle window if no other event arrives.
    if (flushTimer != null) window.clearTimeout(flushTimer);
    flushTimer = window.setTimeout(() => { flush(); }, 1500);
  }
}

/** Force-send the queued events. Used on step advance and unload. */
export function flush(): void {
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const payload = { events: queue };
  queue = [];

  const body = JSON.stringify(payload);
  // Prefer sendBeacon: queued by the browser, never blocks navigation, ideal
  // for analytics. Fall back to fetch with keepalive.
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
  } catch { /* ignore */ }
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => { /* swallow — analytics must never break the wizard */ });
  } catch { /* ignore */ }
}

// ── Helpers used by step modules ────────────────────────────────────────────

/** Begin tracking when a range card scrolls into view. Returns an unobserver
 *  so the caller can clean up. Idempotent per (sessionId, rangeId) thanks to
 *  the `data-tracked` flag we set on the element. */
export function observeRangeCard(
  el: HTMLElement,
  range: { id: string; name: string; supplierId?: string },
): () => void {
  if (typeof IntersectionObserver === "undefined") {
    // Fallback: track once on attach.
    track({
      eventType: "range_viewed",
      rangeId: range.id,
      rangeName: range.name,
      ...(range.supplierId ? { supplierId: range.supplierId } : {}),
    });
    return () => { /* nothing to clean up */ };
  }

  let fired = false;
  const obs = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !fired) {
          fired = true;
          track({
            eventType: "range_viewed",
            rangeId: range.id,
            rangeName: range.name,
            ...(range.supplierId ? { supplierId: range.supplierId } : {}),
          });
          obs.disconnect();
          break;
        }
      }
    },
    { threshold: 0.4 },
  );
  obs.observe(el);
  return () => obs.disconnect();
}
